import { useEffect, useState } from 'react';
import API from '../../api';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import SearchSelect from '../../components/SearchSelect';

export default function EnseignantEvaluations() {
  const [evals, setEvals] = useState([]);
  const [cours, setCours] = useState([]);
  const [form, setForm] = useState({
    titre: '', cours: '', type_eval: 'EXAMEN', date: '', duree_minutes: 60, bareme: 20, description: '', publie: false,
  });
  const [fichierSujet, setFichierSujet] = useState(null);
  const [editId, setEditId] = useState(null);
  const { addToast } = useToast();
  const confirm = useConfirm();

  const load = () => {
    API.get('evaluations/').then((r) => setEvals(r.data.results || r.data));
    API.get('cours/').then((r) => setCours(r.data.results || r.data));
  };
  useEffect(() => { load(); }, []);

  const handleChange = (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({ ...form, [e.target.name]: val });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'duree_minutes' || k === 'bareme') fd.append(k, parseInt(v));
        else fd.append(k, v);
      });
      if (fichierSujet) fd.append('fichier_sujet', fichierSujet);
      if (editId) { await API.put(`evaluations/${editId}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); addToast('Évaluation modifiée', 'success'); }
      else { await API.post('evaluations/', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); addToast('Évaluation ajoutée', 'success'); }
      setForm({ titre: '', cours: '', type_eval: 'EXAMEN', date: '', duree_minutes: 60, bareme: 20, description: '', publie: false });
      setFichierSujet(null);
      setEditId(null);
      load();
    } catch { addToast('Erreur lors de l\'opération', 'error'); }
  };

  const handleEdit = (ev) => {
    setEditId(ev.id);
    setForm({
      titre: ev.titre, cours: ev.cours, type_eval: ev.type_eval,
      date: ev.date?.slice(0, 16) || '', duree_minutes: ev.duree_minutes,
      bareme: ev.bareme, description: ev.description || '', publie: ev.publie,
    });
  };

  const handleDelete = async (id) => {
    if (await confirm('Supprimer cette évaluation ?')) { await API.delete(`evaluations/${id}/`); addToast('Évaluation supprimée', 'info'); load(); }
  };

  const typeBadge = (t) => {
    const map = { QCM: 'badge-qcm', EXAMEN: 'badge-examen', TP: 'badge-tp', INTERRO: 'badge-interro' };
    return <span className={`badge-type ${map[t] || ''}`}>{t}</span>;
  };

  return (
    <div>
      <div className="page-header">
        <h2><i className="bi bi-clipboard-check" /> Évaluations</h2>
      </div>

      <div className="form-glass">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Titre</label>
              <input className="form-control" name="titre" placeholder="Titre de l'évaluation" value={form.titre} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Cours</label>
              <SearchSelect
                name="cours"
                value={form.cours}
                onChange={handleChange}
                options={cours.map((c) => ({ value: c.id, label: c.titre }))}
                placeholder="-- Sélectionner --"
                required
              />
            </div>
            <div className="form-group">
              <label>Type</label>
              <select className="form-control" name="type_eval" value={form.type_eval} onChange={handleChange}>
                <option value="QCM">QCM</option>
                <option value="TP">TP</option>
                <option value="EXAMEN">Examen</option>
                <option value="INTERRO">Interrogation</option>
              </select>
            </div>
            <div className="form-group">
              <label>Date</label>
              <input className="form-control" name="date" type="datetime-local" value={form.date} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Durée (min)</label>
              <input className="form-control" name="duree_minutes" type="number" value={form.duree_minutes} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Barème</label>
              <input className="form-control" name="bareme" type="number" value={form.bareme} onChange={handleChange} required />
            </div>
            <div className="form-group full-width">
              <label>Description</label>
              <textarea className="form-control" name="description" placeholder="Description" value={form.description} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Fichier sujet (optionnel)</label>
              <input className="form-control" type="file" onChange={(e) => setFichierSujet(e.target.files[0])} />
            </div>
            <div className="form-group">
              <label className="form-check">
                <input name="publie" type="checkbox" checked={form.publie} onChange={handleChange} /> Publié
              </label>
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              <i className={editId ? 'bi bi-check-lg' : 'bi bi-plus-lg'} /> {editId ? 'Modifier' : 'Ajouter'}
            </button>
            {editId && (
              <button type="button" className="btn btn-secondary" onClick={() => setEditId(null)}>
                <i className="bi bi-x-lg" /> Annuler
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="data-table-wrapper">
        <table className="data-table">
          <thead><tr><th>Titre</th><th>Cours</th><th>Type</th><th>Date</th><th>Barème</th><th>Statut</th><th>Actions</th></tr></thead>
          <tbody>
            {evals.length === 0 ? (
              <tr><td colSpan="7"><div className="empty-state"><i className="bi bi-clipboard-check" /><p>Aucune évaluation</p></div></td></tr>
            ) : evals.map((ev) => (
              <tr key={ev.id}>
                <td>{ev.titre}</td><td>{ev.cours_titre}</td><td>{typeBadge(ev.type_eval)}</td>
                <td>{new Date(ev.date).toLocaleDateString('fr-FR')}</td>
                <td>{ev.bareme}</td>
                <td>{ev.publie ? <span className="badge-status badge-published"><i className="bi bi-check-circle" /> Publié</span> : <span className="badge-status badge-draft">Brouillon</span>}</td>
                <td>
                  <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(ev)}><i className="bi bi-pencil" /></button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(ev.id)}><i className="bi bi-trash" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
